import {
    useContext,
    useEffect,
    useState,
} from 'react';

import { Trans, useTranslation } from 'react-i18next';
import useLocalStorage from '../../utils/useLocalStorage';

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    FormControlLabel,
    List,
    MenuItem,
    Switch,
    TextField,
} from '@mui/material';

import api from '../../api';
import {
    setLocation,
    setUser,
} from '../../context/actions';
import { Context } from '../../context/context';

import EditWorkTimeModel from './EditWorkTimeModel';
import FlexTimeConfigForm from '../shared/FlexTimeConfigForm';

const SETTINGS = [{
    i18nKey: 'sprache',
    label: 'Sprache',
    value: 'Language'
}, {
    i18nKey: 'standort',
    label: 'Standort',
    value: 'Location'
}, {
    i18nKey: 'arbeitszeitkonto',
    label: 'Arbeitszeitkonto',
    value: 'TimeAccount'
}, {
    i18nKey: 'arbeitszeitmodell',
    label: 'Arbeitszeitmodell',
    value: 'TimeModel'
},
];

function UserSettingsPage() {
    const [{ user }, dispatch] = useContext(Context);
    const [selectedSetting, setSelectedSetting] = useState(SETTINGS[0].value);
    const { i18n } = useTranslation();
    const [language, setLanguage] = useLocalStorage('language', 'de');
    const [locations, setLocations] = useState([]);

    const changeLng = (lng) => {
        i18n.changeLanguage(lng);
    };

    useEffect(() => {
        async function getInstitute() {
            const location = await api.readInstitutesLocations(user.institute);
            setLocations(location);
        }
        if (user.institute !== '') {
            getInstitute();
        }
    }, [user.institute]);

    const handleLanguage = (event) => {
        setLanguage(event.target.value);
        changeLng(event.target.value);
    };

    const handleLocation = (event) => {
        const newLocation = locations.find(l => l.name === event.target.value);
        let update = {
            ...user,
            location: newLocation,
        };
        api.updateUser(user.id, update).then(res => {
            api.readUser(user.id).then(res=> {
                dispatch(setUser(res))
            })
        });
    };

    const updateHasFlexTimeAccount = (event) => {
        const updatedUser = {
            ...user,
            hasFlexTimeAccount: event.target.checked,
        };
        api.updateUser(user.id, updatedUser).then(res => {
            api.readUser(user.id).then(res=> {
                dispatch(setUser(res))
            })
        });
    }; 

    return (
        <Box sx={{ display: 'flex' }}>
            <List sx={{ minWidth: 200, pr: 2 }}>
                {SETTINGS.map(({ value, i18nKey, label }) => {
                    return (
                        (value === 'TimeAccount' || value === 'TimeModel') && !user.isAllowedFlexTimeAccount ? <p></p> :
                        <MenuItem
                            key={value}
                            onClick={() => setSelectedSetting(value)}
                            sx={{ fontWeight: value === selectedSetting ? 'bold' : 'regular' }}
                        >
                            <Trans i18nKey={i18nKey} ns='userSettingsPage'>{label}</Trans>
                        </MenuItem>);
                        
                })}
            </List>
            {selectedSetting === 'Language' &&
                <Card sx={{ width: '100vw', p: 3 }}>
                    <CardHeader title={<Trans i18nKey='sprache' ns='userSettingsPage'>Sprache</Trans>} />
                    <CardContent>
                        <TextField
                            value={language}
                            label={<Trans i18nKey='sprache' ns='userSettingsPage'>Sprache</Trans>}
                            select
                            onChange={handleLanguage}
                            helperText={<Trans i18nKey='wähleEineSpracheAus' ns='userSettingsPage'>Wähle eine Sprache aus.</Trans>}
                        >
                            <MenuItem value={'de'}> <Trans i18nKey='deutsch' ns='userSettingsPage'>Deutsch</Trans> </MenuItem>
                            <MenuItem value={'en'}> <Trans i18nKey='englisch' ns='userSettingsPage'>Englisch</Trans> </MenuItem>
                        </TextField>
                    </CardContent>
                </Card>
            }
            {selectedSetting === 'Location' &&
                <Card sx={{ width: '100vw', p: 3 }}>
                    <CardHeader title={<Trans i18nKey='standort' ns='userSettingsPage'>Standort</Trans>} />
                    <CardContent>
                        <TextField
                            value={user.location.name}
                            label={<Trans i18nKey='standort' ns='userSettingsPage'>Standort</Trans>}
                            select
                            onChange={handleLocation}
                            helperText={<Trans i18nKey='wähleDeinenInstitutsstandortAus' ns='userSettingsPage'>Wähle deinen Institutsstandort aus.</Trans>}
                        >
                            {locations.map((location) => (
                                <MenuItem key={location.name} value={location.name}>{location.name}</MenuItem>
                            ))}
                        </TextField>
                    </CardContent>
                </Card>
            }
            {user.isAllowedFlexTimeAccount && selectedSetting === 'TimeAccount' &&
                <Card sx={{ width: '100vw', p: 3 }}>
                    <CardHeader title={<Trans i18nKey='arbeitszeitkontoTitel' ns='userSettingsPage'>Konfiguration des Arbeitszeitkontos</Trans>} />
                    <CardContent>
                        <FormControlLabel
                            control={<Switch checked={user.hasFlexTimeAccount ?? false} onChange={updateHasFlexTimeAccount} />}
                            label={user.hasFlexTimeAccount
                                ? <Trans i18nKey='aktiviert' ns='userSettingsPage'>Arbeitszeitkonto aktiviert</Trans>
                                : <Trans i18nKey='deaktiviert' ns='userSettingsPage'>Arbeitszeitkonto deaktiviert</Trans>}
                            sx={{ pb: 2 }}
                        />
                        <FlexTimeConfigForm detailed={true} />
                    </CardContent>
                </Card>
            }
            {user.isAllowedFlexTimeAccount && selectedSetting === 'TimeModel' && <EditWorkTimeModel />}
        </Box>
    );
};

export default UserSettingsPage
